import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Colors, Fonts, Spacing, Typography } from '../constants';

interface PageTitleProps {
  title: string;
  subtitle?: string;
  /** Back arrow on the left */
  onBack?: () => void;
  /** Slot on the right (search, a small ghost button, …) */
  right?: React.ReactNode;
  style?: ViewStyle;
}

/**
 * Desktop page heading (design v2): optional back arrow, Lora title (`Typography.title`, 36),
 * muted subtitle, and a right-hand slot for page actions.
 */
export const PageTitle: React.FC<PageTitleProps> = ({ title, subtitle, onBack, right, style }) => {
  const { t } = useTranslation();
  return (
    <View style={[styles.row, style]}>
      {onBack ? (
        <TouchableOpacity
          onPress={onBack}
          style={styles.back}
          accessibilityRole="button"
          accessibilityLabel={t('common.buttons.back')}
        >
          <Ionicons name="arrow-back" size={24} color={Colors.textDark} />
        </TouchableOpacity>
      ) : null}
      <View style={styles.text}>
        <Text style={[Typography.title, styles.title]}>{title}</Text>
        {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
      </View>
      {right}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Spacing.md,
    marginBottom: Spacing.xl,
  },
  back: {
    padding: Spacing.xs,
    marginTop: Spacing.xs,
  },
  text: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    color: Colors.textDark,
  },
  subtitle: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    lineHeight: 23,
    color: Colors.textMedium,
    marginTop: Spacing.xs,
  },
});
